import { Injectable, inject } from '@angular/core';
import { Map, geoJSON, Layer, LatLng, Direction } from 'leaflet';
import { MapStore, IdentifiableLayer } from '../../stores/map/map.state';
import { MapRenderService } from './map-render.service';
import { MapLegendService } from './map-legend.service';
import { MapEventService, FeatureInfo } from './map-event.service';
import { MapTooltipService } from './map-tooltip.service';
import { MapDataHelper } from './map-data.helper';
import { LeafletMapboxService } from '../leaflet-mapbox.service';
import { GridStore } from '../../stores/grid/grid.state';
import { DataLayerTypeStore } from '../../stores/data-layer-type/data-layer-type.state';
import { IndicatorDataStore } from '../../stores/indicatpr-data/indicator-data.state';
import { PoiConf, WidgetConfig } from '../grid-widget-config.service';
import { DataEntry } from '../../models/indicator-data';
import { Feature, FeatureCollection } from '../../models/geo-data';
import { Indicator } from '../../models/indicator';
import { Scale } from '../../models/scale';
import { DataLayerTypeIcon } from '../../stores/data-layer-type/data-layer-type.selectors';
import { ExtendedChartData } from '../../models/chartjs-extensions';
import { debounceIntent } from '../../utility/app.utility';
import { isNumber } from 'chart.js/helpers';
import { getUnit, isWegweiserIndicator } from '../../models/indicator';
import { IndicatorStore } from '../../stores/indicator/indicator.state';

/**
 * MapCoordinator - Orchestrates map operations using services and store
 *
 * This service coordinates complex map workflows by:
 * - Using MapStore for state management
 * - Using MapRenderService for rendering
 * - Using MapLegendService for legends
 * - Using MapEventService for events
 * - Using MapTooltipService for tooltips
 * - Using MapDataHelper for data operations
 */
@Injectable()
export class MapCoordinatorService {
  private readonly mapStore = inject(MapStore);
  private readonly indicatorStore = inject(IndicatorStore);
  private readonly renderService = inject(MapRenderService);
  private readonly legendService = inject(MapLegendService);
  private readonly eventService = inject(MapEventService);
  private readonly tooltipService = inject(MapTooltipService);
  private readonly dataHelper = inject(MapDataHelper);
  private readonly mapboxService = inject(LeafletMapboxService);
  private readonly gridStore = inject(GridStore);
  private readonly layerTypeStore = inject(DataLayerTypeStore);
  private readonly dataStore = inject(IndicatorDataStore);

  /**
   * Initializes a map instance
   */
  initializeMap(mapContainerId: string, mapContainer: HTMLElement): Map {
    const map = this.mapboxService.initializeMap(mapContainer);
    this.mapStore.setMap(map, mapContainerId);
    return map;
  }

  /**
   * Configures the map settings based on the provided configuration object.
   *
   * @param {WidgetConfig} config - The configuration object containing details such as indicator, data layer, colorset, and map properties.
   * @return {void} - This method does not return a value.
   */
  setMapConfig(config: WidgetConfig): void {
    const { indicator, indicatorData } = this.dataHelper.getIndicatorInfo(
      config.indicator,
      config.dataLayer
    );

    const { colorset, colors } = this.dataHelper.getColors(
      indicatorData,
      indicator,
      config.colorset,
      config.map?.colorset,
      config.map?.legend.invert
    );

    // Handle tile layer change if needed
    const currentTileLayerType = this.mapStore.tileLayerType();
    if (currentTileLayerType !== config.map?.tileLayer) {
      this.changeTileLayer(config.map?.tileLayer || 'light');
    }

    this.mapStore.updateMapState({
      config,
      colorset,
      colors,
      indicator,
      indicatorData,
    });
  }

  /**
   * Updates the map's tile layer based on the specified type.
   *
   * @param {string} type - The type of tile layer to switch to. If set to 'blank', removes the current tile layer.
   * @return {void} This method does not return any value.
   */
  changeTileLayer(type: string): void {
    const map = this.mapStore.map();
    const currentTileLayer = this.mapStore.tileLayer();

    if (!map) return;

    if (currentTileLayer) {
      this.mapboxService.clearLayers(map, currentTileLayer, true);
      map.removeLayer(currentTileLayer);
    }

    if (type === 'blank') {
      this.mapStore.setTileLayer(null, type);
      return;
    }

    const newTileLayer = this.mapboxService.createFreshMapboxGLLayer(type);
    newTileLayer.addTo(map);
    map.invalidateSize();
    this.mapStore.setTileLayer(newTileLayer, type);
  }

  /**
   * Creates or updates a GeoJSON layer on the map based on the provided configuration and data.
   *
   * @param {DataEntry | undefined} data - The data entry object containing the values used for the map layer.
   * @param {FeatureCollection} geoJsonData - The GeoJSON feature collection representing the geographic features.
   * @param {WidgetConfig} config - Configuration object defining layer and visualization settings.
   * @param {ExtendedChartData} [chartData] - Optional extended chart data for additional layer customization.
   * @param {any} [icon] - Optional icon object used for point layers, if applicable.
   * @return {void} This method does not return a value.
   */
  createGeoJsonLayer(
    data: DataEntry | DataEntry[] | undefined,
    geoJsonData: FeatureCollection,
    config: WidgetConfig,
    chartData?: ExtendedChartData,
    icon?: any
  ): void {
    const map = this.mapStore.map();
    if (!map || !geoJsonData) return;

    const currentGeoJsonLayer = this.mapStore.geoJsonLayer();
    const { indicator, indicatorData } = this.dataHelper.getIndicatorInfo(
      config.indicator,
      config.dataLayer
    );

    const firstData = Array.isArray(data) ? data[0] : data;

    const scales = this.dataHelper.getScales(
      indicatorData,
      indicator,
      firstData,
      this.mapStore.colors(),
      {
        legendScope: config.map?.legend.legendScope,
        scaleMode: config.map?.legend.scaleMode,
        numberOfScales: config.map?.legend.numberOfScales,
      }
    );

    // Highlight changed: update styles in place; otherwise recreate the layer
    if (currentGeoJsonLayer && !this.checkHighlightChange()) {
      map.removeLayer(currentGeoJsonLayer);
    } else if (currentGeoJsonLayer && !Array.isArray(data)) {
      this.updateExistingLayerStyles(currentGeoJsonLayer, data, scales);
      this.mapStore.updateMapState({
        scales,
        indicator,
        data,
        mouseOverFeatureKey:
          this.gridStore.options()?.highlightedFeature?.key || null,
        comparisonValues: config.groupedData?.comparisonValues || null,
      });
      return;
    }

    // Create new layer
    const newGeoLayer = icon
      ? this.createPointLayerForGeoJson(config, data, geoJsonData, scales, icon)
      : this.createPolygonLayer(
          geoJsonData,
          firstData,
          scales,
          config,
          indicator,
          chartData
        );

    newGeoLayer.addTo(map);

    this.mapStore.updateMapState({
      geoJsonLayer: newGeoLayer,
      scales,
      indicator,
      data: firstData,
      mouseOverFeatureKey:
        this.gridStore.options()?.highlightedFeature?.key || null,
      comparisonValues: config.groupedData?.comparisonValues || null,
    });
  }

  /**
   * Creates a polygon layer on the map using the given GeoJSON data and configuration parameters.
   *
   * @param {FeatureCollection} geoJsonData - The GeoJSON feature collection representing the geometries to be rendered.
   * @param {DataEntry | undefined} data - Optional data entries associated with the features for rendering and interactions.
   * @param {Scale[] | undefined} scales - Optional array of scale objects used for visualizing data on the map.
   * @param {WidgetConfig} config - The configuration object containing map and widget-specific settings.
   * @param {Indicator | null} indicator - The indicator object used for processing feature-specific information, or null if not applicable.
   * @param {ExtendedChartData} [chartData] - Optional chart data used to enhance feature interactions or visualizations.
   * @return {any} The created polygon layer with applied styles, interactivity, and other configurations.
   */
  private getHighlightState(): {
    highlightedKey: string | null;
    selectedKeys: string[];
  } {
    return {
      highlightedKey: this.gridStore.options()?.highlightedFeature?.key || null,
      selectedKeys:
        this.gridStore.options()?.selectedFeatures?.map(f => String(f.key)) ||
        [],
    };
  }

  private createPolygonLayer(
    geoJsonData: FeatureCollection,
    data: DataEntry | undefined,
    scales: Scale[] | undefined,
    config: WidgetConfig,
    indicator: Indicator | null,
    chartData?: ExtendedChartData
  ): any {
    const colors = this.mapStore.colors();
    const { highlightedKey, selectedKeys } = this.getHighlightState();

    return geoJSON(geoJsonData, {
      pane: 'overlayPane',
      style: feature =>
        this.renderService.styleFn(
          feature,
          data,
          scales,
          colors,
          highlightedKey,
          selectedKeys,
          config.map?.districtOpacity
        ),
      onEachFeature: (feature, layer) => {
        this.attachFeatureInteractions(
          feature,
          layer,
          data,
          config,
          indicator,
          chartData
        );
      },
    });
  }

  /**
   * Creates a point layer for the given GeoJSON data.
   *
   * @param {WidgetConfig} config - The configuration object for the widget, including settings for the data layer type, legend, and map properties.
   * @param {DataEntry | undefined} data - An optional data entry object to include in the point layer. If undefined, no data will be used.
   * @param {FeatureCollection} geoJsonData - The GeoJSON feature collection representing the spatial data.
   * @param {Scale[] | undefined} scales - An optional array of scale configurations to apply to the point layer. If undefined, no scales will be applied.
   * @param {any} icon - The icon or marker symbol to be used for the point layer.
   * @return {any} The created point layer instance.
   */
  private createPointLayerForGeoJson(
    config: WidgetConfig,
    data: DataEntry | DataEntry[] | undefined,
    geoJsonData: FeatureCollection,
    scales: Scale[] | undefined,
    icon: any
  ): any {
    const poiConfig: PoiConf = {
      type: config.dataLayerType,
      legend: {
        colorset: config.map?.colorset || config.colorset,
        legendScope: config.map?.legend?.legendScope,
        invert: config.map?.legend?.invert,
        numberOfScales: config.map?.legend?.numberOfScales,
        scaleMode: config.map?.legend?.scaleMode,
      },
    };

    const indicators =
      Array.isArray(data) && data.length > 1
        ? (config.indicators
            .map(id => this.indicatorStore.getById(id))
            .filter(i => !!i) as Indicator[])
        : undefined;

    const scalesArray =
      indicators && Array.isArray(data)
        ? this.generateScalesForIndicators(indicators, data, poiConfig, config)
        : undefined;

    return this.renderService.createPointLayer(
      poiConfig,
      Array.isArray(data) ? data : data ? [data] : undefined,
      geoJsonData,
      scalesArray || (scales ? [scales] : undefined),
      icon,
      indicators,
      this.mapStore.colors(),
      config.map?.poiSize || 1
    );
  }

  /**
   * Attaches interactive features, such as tooltips, charts, and event listeners, to a map feature.
   *
   * @param {any} feature - The feature object representing a geographic element on the map.
   * @param {Layer} layer - The map layer associated with the feature for binding tooltips and events.
   * @param {DataEntry | undefined} data - The data object containing values to display for the feature.
   * @param {WidgetConfig} config - Configuration settings for the widget, including map and tooltip options.
   * @param {Indicator | null} indicator - An optional indicator object providing metadata and units for the values.
   * @param {ExtendedChartData} [chartData] - Optional dataset for generating on-map charts associated with the feature.
   * @return {void} This method does not return a value.
   */
  private attachFeatureInteractions(
    feature: any,
    layer: Layer,
    data: DataEntry | undefined,
    config: WidgetConfig,
    indicator: Indicator | null,
    chartData?: ExtendedChartData
  ): void {
    if (!feature.properties) return;

    const key = feature.properties?.key || feature.id;
    const value = data && data[key] && isNumber(data[key]) ? data[key] : null;

    // Create tooltip content
    const popupContent = this.tooltipService.generateLeafletTooltip(
      feature,
      value,
      config.map?.captions,
      indicator?._id || '',
      indicator ? getUnit(indicator) : '',
      indicator ? isWegweiserIndicator(indicator) : false
    );

    const content = popupContent || document.createElement('div');

    // Add chart if needed
    const featureKey = String(key);
    const isDisabled = config.disabledCharts?.includes(featureKey);
    const shouldHaveChart =
      config.map?.captions.chart &&
      chartData &&
      chartData.datasets &&
      chartData.datasets.length > 1 &&
      !isDisabled;

    if (shouldHaveChart) {
      const chartContent = this.tooltipService.generateOnMapChart(
        this.mapStore._id(),
        feature,
        config,
        chartData,
        this.mapStore.colors()
      );
      if (chartContent) content.append(chartContent);
    }

    // Bind tooltip if we have content
    if (popupContent || shouldHaveChart) {
      const tooltipStyle = config.map?.captions.style;
      layer.bindTooltip(content, {
        permanent: true,
        direction: (tooltipStyle === 'blank'
          ? 'center'
          : tooltipStyle || 'top') as Direction,
      });
      setTimeout(() => {
        layer.getTooltip()?.update();
      }, 50);
    }

    // Attach events
    const DEBOUNCE_MS = 200;
    const intent = debounceIntent(
      (e: any) => this.handleHighlightFeature(e),
      DEBOUNCE_MS
    );

    layer.on({
      mouseover: intent.mouseover,
      mouseout: (e: any) => {
        intent.mouseout(e);
        this.handleResetHighlight();
      },
      click: (e: any) => this.eventService.selectDistrictAndZoomToFeature(e),
    });
  }

  /**
   * Handles the highlight feature event by invoking the highlightFeature method from the event service.
   * It processes the event, retrieves necessary parameters from the map store, and updates the feature highlight state.
   *
   * @param e - The event object containing data related to the interaction.
   * @return void - Does not return a value.
   */
  private handleHighlightFeature(e: any): void {
    this.eventService.highlightFeature(
      e,
      this.mapStore._id(),
      this.mapStore.indicator(),
      this.mapStore.data(),
      this.mapStore.featureHighlight()?.feature.properties.key || null,
      (featureInfo: FeatureInfo) => {
        this.mapStore.setFeatureHighlight(featureInfo);
      }
    );
  }

  /**
   * Resets the highlight of a feature on the map by calling the resetHighlight method
   * on the event service. If a feature is currently highlighted, its key is retrieved and
   * passed to the call. After the reset, the feature highlight in the map store is cleared.
   *
   * @return {void} No value is returned from this method.
   */
  private handleResetHighlight(): void {
    this.eventService.resetHighlight(
      this.mapStore._id(),
      this.mapStore.featureHighlight()?.feature.properties.key || null,
      () => {
        this.mapStore.setFeatureHighlight(null);
      }
    );
  }

  /**
   * Updates the styles of the given layer based on the provided data, scales, and configuration values.
   * This method traverses through all sub-layers and applies a new style to each based on the provided parameters.
   *
   * @param layer The map layer whose styles need to be updated. Expected to support iterating through sub-layers and setting their styles.
   * @param data An instance of `DataEntry` containing data relevant for styling the layer. Can be `undefined` if no data is provided.
   * @param scales An array of `Scale` objects used to compute styling-related properties like color and size. Can be `undefined` if no scales are provided.
   * @return void Does not return a value. Updates the styles of sub-layers in place.
   */
  private updateExistingLayerStyles(
    layer: any,
    data: DataEntry | undefined,
    scales: Scale[] | undefined
  ): void {
    const colors = this.mapStore.colors();
    const { highlightedKey, selectedKeys } = this.getHighlightState();
    const districtOpacity = this.mapStore.config()?.map?.districtOpacity;

    layer.eachLayer((subLayer: any) => {
      if (typeof subLayer.setStyle === 'function' && 'feature' in subLayer) {
        subLayer.setStyle(
          this.renderService.styleFn(
            subLayer.feature,
            data,
            scales,
            colors,
            highlightedKey,
            selectedKeys,
            districtOpacity
          )
        );
      }
    });
  }

  /**
   * Determines if there has been a change in the highlighted feature.
   *
   * The method compares the current mouse-over feature key from the mapStore
   * with the highlighted feature key from the gridStore options. If the keys
   * are different, it indicates that the highlight has changed.
   *
   * @return {boolean} True if the highlighted feature has changed, otherwise false.
   */
  checkHighlightChange(): boolean {
    const oldHighlightKey = this.mapStore.mouseOverFeatureKey() || null;
    const newHighlightKey =
      this.gridStore.options()?.highlightedFeature?.key || null;

    return String(oldHighlightKey) !== String(newHighlightKey);
  }

  /**
   * Adds a point-of-interest (POI) layer to the map.
   *
   * @param {PoiConf} poiConfig - Configuration object for the point layer, including its type and styling options.
   * @param {string} typeName - A unique name representing the type of the POI layer.
   * @param {DataEntry[] | undefined} data - An optional array containing data entries related to the POI layer.
   * @param {FeatureCollection} geoJsonData - The GeoJSON feature collection defining the spatial data for the layer.
   * @param {DataLayerTypeIcon} icon - The icon configuration for rendering point markers on the map.
   * @param {Indicator[] | undefined} indicators - An optional array of indicators used for visual representation and scaling.
   * @return {void} Does not return any value. Modifies the map by adding the configured POI layer.
   */
  addPointLayer(
    poiConfig: PoiConf,
    typeName: string,
    data: DataEntry[] | undefined,
    geoJsonData: FeatureCollection,
    icon: DataLayerTypeIcon,
    indicators: Indicator[] | undefined
  ): void {
    const map = this.mapStore.map();
    if (!map || !geoJsonData) return;

    const config = this.mapStore.config();
    if (!config) return;

    const poiId = poiConfig.type;

    // Remove existing layer if present
    const existingLayer = this.mapStore.getLayer(poiId);
    if (existingLayer) {
      map.removeLayer(existingLayer);
    }

    // Generate scales for indicators
    const scalesArray = this.generateScalesForIndicators(
      indicators,
      data,
      poiConfig,
      config
    );

    // Create the point layer
    const layer = this.createPoiLayer(
      poiConfig,
      typeName,
      data,
      geoJsonData,
      scalesArray,
      icon,
      indicators
    );

    layer.addTo(map);

    // Add legend
    this.addPoiLegend(map, indicators, scalesArray, icon, poiConfig, poiId);

    // Store layer
    const identifiableLayer: IdentifiableLayer = Object.assign(layer, {
      id: poiId,
      scales: scalesArray[0],
      indicator: indicators?.[0],
    });
    this.mapStore.addLayer(identifiableLayer);
  }

  /**
   * Generates scales for the provided indicators based on the given data, POI configuration, and widget configuration.
   *
   * @param {Indicator[] | undefined} indicators - An array of indicators for which scales need to be generated.
   *                                               If undefined or invalid, an empty array will be returned.
   * @param {DataEntry[] | undefined} data - The data entries corresponding to the indicators. Should align with the indicators' order.
   *                                          If undefined or invalid, an empty array will be returned.
   * @param {PoiConf} poiConfig - Configuration for Points of Interest (POI), which includes settings such as colors and scale modes.
   * @param {WidgetConfig} config - Configuration for the widget, including data layer and legend settings.
   * @return {Scale[][]} A nested array of scales corresponding to each indicator. Returns an empty array if the input data is invalid or no scales are generated.
   */
  private generateScalesForIndicators(
    indicators: Indicator[] | undefined,
    data: DataEntry[] | undefined,
    poiConfig: PoiConf,
    config: WidgetConfig
  ): Scale[][] {
    const scalesArray: Scale[][] = [];

    if (!indicators || !indicators.length || !data || !data.length) {
      return scalesArray;
    }

    const poiColors = this.dataHelper.getPoiColors(
      poiConfig,
      this.mapStore.colors()
    );

    indicators.forEach((indicator, index) => {
      const indicatorData = this.dataStore.getByDataLayerAndIndicator(
        config.dataLayer,
        indicator._id
      );

      const existingLegend = this.mapStore.getIndicatorLegend(indicator._id);
      const existingScales = existingLegend?.scales;

      const scales = this.dataHelper.getScales(
        indicatorData,
        indicator,
        data[index],
        poiColors,
        {
          legendScope: config.map?.legend?.legendScope,
          scaleMode:
            poiConfig?.legend?.scaleMode || config.map?.legend.scaleMode,
          numberOfScales:
            poiConfig?.legend?.numberOfScales ||
            config.map?.legend.numberOfScales,
        },
        existingScales
      );

      if (scales) {
        scalesArray.push(scales);
      }
    });

    return scalesArray;
  }

  /**
   * Creates and returns a point of interest (POI) layer based on the provided configuration and data.
   *
   * @param {PoiConf} poiConfig - The POI configuration object containing settings for the layer.
   * @param {string} typeName - The type name of the POI to be used in tooltips and layer properties.
   * @param {DataEntry[] | undefined} data - The dataset associated with the POI layer, if any.
   * @param {FeatureCollection} geoJsonData - The GeoJSON data used to define the geographical features of the POI layer.
   * @param {Scale[][]} scalesArray - Array of scales associated with the POI layer for rendering purposes.
   * @param {DataLayerTypeIcon} icon - The icon representing the data layer type for the POI layer.
   * @param {Indicator[] | undefined} indicators - The indicators associated with the POI, if any, for additional information.
   * @return {any} The resulting POI layer object created by the render service.
   */
  private createPoiLayer(
    poiConfig: PoiConf,
    typeName: string,
    data: DataEntry[] | undefined,
    geoJsonData: FeatureCollection,
    scalesArray: Scale[][],
    icon: DataLayerTypeIcon,
    indicators: Indicator[] | undefined
  ): any {
    const colors = this.dataHelper.getPoiColors(
      poiConfig,
      structuredClone(this.mapStore.colors())
    );
    return this.renderService.createPointLayer(
      poiConfig,
      data,
      geoJsonData,
      scalesArray,
      icon,
      indicators,
      colors,
      this.mapStore.config()?.map?.poiSize || 1,
      (feature: Feature, layer: Layer) => {
        const type = this.layerTypeStore.getById(poiConfig.type);
        const popupContent = this.tooltipService.buildPoiPopup(
          type?.name || typeName,
          feature,
          icon.svg,
          data,
          indicators,
          icon.color
        );
        layer.bindPopup(popupContent);
      }
    );
  }

  /**
   * Adds a legend to the map for a Point of Interest (POI).
   * Depending on the input parameters, this function determines whether to display
   * a simple legend for multiple indicators or a scale-based legend for a single indicator.
   *
   * @param {Map} map The map instance on which the legend will be added.
   * @param {Indicator[] | undefined} indicators An array of indicators for the POI, or undefined if no indicators are available.
   * @param {Scale[][]} scalesArray An array of arrays representing scales associated with the indicators.
   * @param {DataLayerTypeIcon} icon The icon representing the data layer for the POI.
   * @param {PoiConf} poiConfig The configuration settings for the Point of Interest.
   * @param {string} poiId The unique identifier for the Point of Interest.
   * @return {void} This method does not return a value.
   */
  private addPoiLegend(
    map: Map,
    indicators: Indicator[] | undefined,
    scalesArray: Scale[][],
    icon: DataLayerTypeIcon,
    poiConfig: PoiConf,
    poiId: string
  ): void {
    const position =
      this.mapStore.config()?.map?.legend.position || 'bottomright';

    if (indicators && indicators.length > 1) {
      // Multiple indicators: simple legend
      this.addMultiIndicatorLegend(
        map,
        indicators,
        poiConfig,
        icon,
        poiId,
        position
      );
    } else if (indicators && indicators.length === 1 && scalesArray.length) {
      // Single indicator: scale-based legend
      this.addSingleIndicatorLegend(
        map,
        indicators[0],
        scalesArray[0],
        icon,
        poiConfig,
        position
      );
    }
  }

  /**
   * Adds a multi-indicator legend to the map, replacing an existing single-indicator legend
   * if applicable. Updates the map store and removes outdated legends as necessary.
   *
   * @param {Map} map - The map instance to which the legend should be added.
   * @param {Indicator[]} indicators - Array of indicators representing the data for the legend.
   * @param {PoiConf} poiConfig - Configuration object for Points of Interest (POI).
   * @param {DataLayerTypeIcon} icon - Icon associated with the indicators.
   * @param {string} poiId - Unique identifier for the POI linked to the legend.
   * @param {any} position - Position information for placing the legend on the map.
   * @return {void} This method does not return any value.
   */
  private addMultiIndicatorLegend(
    map: Map,
    indicators: Indicator[],
    poiConfig: PoiConf,
    icon: DataLayerTypeIcon,
    poiId: string,
    position: any
  ): void {
    const indicatorColors = this.renderService.getIndicatorColors(
      indicators,
      this.dataHelper.getPoiColors(poiConfig, this.mapStore.colors())
    );

    const existingLegend = this.mapStore.getIndicatorLegend(poiId);

    // Remove old single-indicator legend if switching from 1 to multiple
    const singleLegend = this.mapStore.getIndicatorLegend(indicators[0]._id);
    if (singleLegend) {
      this.legendService.removeLegend(map, singleLegend);
      this.mapStore.removeIndicatorLegend(indicators[0]._id);
    }

    const indicatorsChanged =
      existingLegend &&
      existingLegend.legendComponentRef.instance.indicators?.length !==
        indicators.length;

    if (!existingLegend || indicatorsChanged) {
      if (existingLegend) {
        this.legendService.removeLegend(map, existingLegend);
        this.mapStore.removeIndicatorLegend(poiId);
      }

      const legend = this.legendService.addSimpleIndicatorLegend(
        map,
        indicators,
        indicatorColors,
        icon,
        position
      );

      this.mapStore.addIndicatorLegend(poiId, {
        ...legend,
        scales: undefined,
        icons: [icon],
      });
    }
  }

  /**
   * Adds a single indicator legend to the map, replacing any existing legends associated
   * with the specified POI. Removes the multi-indicator legend if switching to a single indicator.
   *
   * @param map The target map instance where the legend will be added.
   * @param indicator The indicator data associated with the legend.
   * @param scales An array of scale objects used to visualize the legend.
   * @param icon The icon representing the data layer type for the legend.
   * @param poiConfig The configuration settings for the Point of Interest.
   * @param position The position where the legend should be displayed on the map.
   * @return void
   */
  private removeMultiLegendIfPresent(map: Map, poiId: string): void {
    const multiLegend = this.mapStore.getIndicatorLegend(poiId);
    if (multiLegend) {
      this.legendService.removeLegend(map, multiLegend);
      this.mapStore.removeIndicatorLegend(poiId);
    }
  }

  private addSingleIndicatorLegend(
    map: Map,
    indicator: Indicator,
    scales: Scale[],
    icon: DataLayerTypeIcon,
    poiConfig: PoiConf,
    position: any
  ): void {
    const poiId = poiConfig.type;

    // Remove multi-indicator legend if switching from multiple to 1
    this.removeMultiLegendIfPresent(map, poiId);

    const existingLegend = this.mapStore.getIndicatorLegend(indicator._id);

    // Remove old indicator legend
    if (existingLegend) {
      this.legendService.removeLegend(map, existingLegend);
    }

    const legend = this.legendService.addLegendComponent(
      map,
      scales,
      this.dataHelper.getPoiColors(poiConfig, this.mapStore.colors()),
      indicator,
      [icon],
      position
    );

    this.mapStore.addIndicatorLegend(poiId, {
      ...legend,
      scales,
      icons: [icon],
    });
  }

  /**
   * Adds a single indicator legend to the map, replacing any existing legends associated
   * with the specified POI or indicator. Removes the multi-indicator legend if switching to a single indicator.
   *
   * @param map The target map instance where the legend will be added.
   * @param indicator The indicator data associated with the legend.
   * @param scales An array of scale objects used to visualize the legend.
   * @param icon The icon representing the data layer type for the legend.
   * @param poiConfig The configuration settings for the Point of Interest.
   * @param position The position where the legend should be displayed on the map.
   * @return void
   */
  private addCombinedIndicatorLegend(
    map: Map,
    indicator: Indicator,
    scales: Scale[],
    icon: DataLayerTypeIcon,
    poiConfig: PoiConf,
    position: any
  ): void {
    const poiId = poiConfig.type;

    // Remove multi-indicator legend if switching from multiple to 1
    this.removeMultiLegendIfPresent(map, poiId);

    const existingLegend = this.mapStore.getIndicatorLegend(indicator._id);
    const existingIcons = existingLegend?.icons || [];

    // Remove old legend
    if (existingLegend) {
      this.legendService.removeLegend(map, existingLegend);
    }

    // Make icons unique based on color+key properties
    const allIcons = [...existingIcons, icon];
    const uniqueIcons = allIcons.filter(
      (icon, index, self) =>
        index ===
        self.findIndex(i => i.color === icon.color && i.key === icon.key)
    );

    const legend = this.legendService.addLegendComponent(
      map,
      scales,
      this.dataHelper.getPoiColors(poiConfig, this.mapStore.colors()),
      indicator,
      uniqueIcons,
      position
    );

    this.mapStore.addIndicatorLegend(poiId, {
      ...legend,
      scales,
      icons: uniqueIcons,
    });
  }

  /**
   * Removes a point layer from the map using the provided layer ID.
   *
   * @param {string} layerId - The unique identifier of the point layer to be removed.
   * @return {void} This method does not return a value.
   */
  removePointLayer(layerId: string): void {
    const map = this.mapStore.map();
    if (!map) return;

    const layer = this.mapStore.getLayer(layerId);
    if (layer) {
      map.removeLayer(layer);
    }

    this.mapStore.removeLayer(layerId);
  }

  /**
   * Removes all point layers and their associated legends from the map.
   *
   * This method iterates through all layers and legends in the map store,
   * removes them from the map, and clears any references to them in the
   * map store.
   *
   * @param {boolean} keepIsochrones If true, layers with an ID starting with 'isochrone-' will not be removed.
   * @return {void} No return value.
   */
  removeAllPointLayers(keepIsochrones = false): void {
    const map = this.mapStore.map();
    if (!map) return;

    // Remove all layers
    const layers = this.mapStore.entityMap();
    Object.keys(layers).forEach(layerId => {
      if (keepIsochrones && layerId.startsWith('isochrone-')) {
        return;
      }

      const layer = layers[layerId];
      if (layer) {
        map.removeLayer(layer);
      }
      this.mapStore.removeLayer(layerId);
    });

    // Remove all legends
    Object.keys(this.mapStore.indicatorLegends()).forEach(indicatorId => {
      const legend = this.mapStore.getIndicatorLegend(indicatorId);
      if (legend) {
        this.legendService.removeLegend(map, legend);
      }
      this.mapStore.removeIndicatorLegend(indicatorId);
    });
  }
}
